import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { RayCodeAction } from '../../actions/RayCodeAction';
import DatePicker from "react-datepicker";
import { ProductConstant } from '../../actions/constants/ProductConstant';
import { AuthAction } from '../../actions';
import { Translate as t } from '../../config/Translate';
import { applyHeaderPrefs, canHideHeaderColumn, toSavedHeaderPrefs, visibleHeaderColumns } from '../../lib/headerPrefs';

// IMP-316 — code 의 leaf(마지막 '.' 뒤) 를 반환. '.' 없으면 code 그대로.
const leafOf = (code) => {
	if (typeof code !== 'string') return code;
	const idx = code.lastIndexOf('.');
	return idx === -1 ? code : code.substring(idx + 1);
};

// IMP-316 — 헤더 선택 Picker(react-select) 용 그룹핑 순수 함수.
// options 를 groupOrder 기준으로 정렬된 그룹 배열로 변환하고,
// base 컬럼 제외(완전일치 OR leaf 매칭, selectedNames 예외) + code 전역 dedup 을 수행한다.
export const buildGroupedHeaderOptions = (options, groupOrder = [], baseHeaderNames = [], selectedNames = [], defaultGroup = '기타') => {
	if (!Array.isArray(options)) return [];

	const seenCodes = new Set();
	const groupMap = new Map(); // groupLabel -> options[]
	const groupAppearanceOrder = [];

	options.forEach((item) => {
		if (!item) return;

		const { code } = item;

		// IMP-316: code 없는 항목은 그룹핑/dedup 대상에서 제외(방어).
		if (code === undefined || code === null || code === '') return;

		if (seenCodes.has(code)) return;

		const isBaseExact = baseHeaderNames.includes(code);
		// IMP-316: dotted base name 은 완전일치만(접두사 다른 옵션의 우연한 leaf 충돌 과다제외 방지).
		// 단일 세그먼트 base(예: 'prdtype')만 옵션 leaf 와 매칭(extradata.prdtype 등 동일 key 충돌 제외 목적).
		const isBaseLeaf = baseHeaderNames.some((baseName) => typeof baseName === 'string' && baseName.indexOf('.') === -1 && baseName === leafOf(code));
		const isExcludedByBase = isBaseExact || isBaseLeaf;
		const isSelected = selectedNames.includes(code);

		if (isExcludedByBase && !isSelected) return;

		seenCodes.add(code);

		const groupLabel = item.group || defaultGroup;

		if (!groupMap.has(groupLabel)) {
			groupMap.set(groupLabel, []);
			groupAppearanceOrder.push(groupLabel);
		}
		groupMap.get(groupLabel).push(item);
	});

	const orderedLabels = [
		...groupOrder.filter((label) => groupMap.has(label)),
		...groupAppearanceOrder.filter((label) => !groupOrder.includes(label)),
	];

	return orderedLabels.map((label) => ({ label, options: groupMap.get(label) }));
};

// BUG-155 — 목록(1행=제품1건)에서 단일 셀로 렌더 불가능한 배열 subdocument 경로 필드 접두사.
// 헤더 컬럼 추가 옵션에서 제외(필터·상세 전용). 목록의 배열 표현은 count 컬럼(cnt_errs 등)이 담당.
// [유지보수] productModel(rayguard-api src/libs/models/productModel.js)의 배열 필드에서 파생된
//   denylist. 모델에 신규 배열 subdocument 필드 추가 시 이 목록도 갱신 필수(미갱신 시 공백 재발).
//   근본 개선(카탈로그 datatype 기반 positive-signal)은 후속 이슈에서 검토.
export const ARRAY_SUBDOC_PREFIXES = ['errs.', 'waranties.', 'comments.', 'cautions.', 'acquisitions.', 'qcs.', 'autoDiagnoses.', 'dimlogs.', 'doalogs.', 'snhistories.', 'sitehistories.', 'exWarranty.'];

// BUG-155 — code 가 헤더 컬럼으로 표시 가능한지(배열 subdocument 경로가 아닌지) 판정.
export const isDisplayableHeaderOption = (code) => {
	if (!code || typeof code !== 'string') return false;
	return !ARRAY_SUBDOC_PREFIXES.some((p) => code.startsWith(p));
};

// IMP-444 — baseHeader prop 기본값. 매 렌더마다 새 배열 레퍼런스가 생기면 이를 deps 로 쓰는
// effect 가 무한 재실행되므로(BUG) 모듈 스코프의 안정된 참조 하나만 재사용한다.
const EMPTY_BASE_HEADER = [];

// IMP-444 — 두 헤더 목록이 "동일"한지 판정하는 순수 비교 함수.
// 길이 + 각 인덱스의 name/label/visible 이 모두 같으면 동일로 본다(순서까지 비교 대상).
// setUnified 갱신 시 이 함수로 bail-out 하여, deps 가 매 렌더 새 레퍼런스로 바뀌어도
// 계산 결과가 실질적으로 같으면 state 를 그대로 두어 무한 재렌더를 막는다.
const isSameHeaderList = (a, b) => {
	if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
	if (a.length !== b.length) return false;
	return a.every((item, idx) => {
		const other = b[idx];
		return item?.name === other?.name && item?.label === other?.label && item?.visible === other?.visible;
	});
};

// IMP-444 (#403) — 기본 컬럼 + 사용자 추가 컬럼을 하나의 목록으로 합치고 저장된 선호값
// (순서/표시)을 적용한다. name 기준 중복 제거 시 먼저 나온 쪽(기본 컬럼)이 우선한다.
// 비배열/undefined 입력에 throw 하지 않는다.
export const buildUnifiedHeaderList = (baseHeader, selectedColumns, savedPrefs) => {
	const base = Array.isArray(baseHeader) ? baseHeader : [];
	const selected = Array.isArray(selectedColumns) ? selectedColumns : [];

	const seen = new Set();
	const merged = [];
	[...base, ...selected].forEach((col) => {
		if (!col || typeof col.name !== 'string' || seen.has(col.name)) return;
		seen.add(col.name);
		merged.push(col);
	});

	return applyHeaderPrefs(merged, savedPrefs);
};

// IMP-444 — 드래그 재배치: fromName 항목을 배열에서 빼서 toName 이 있던 자리로 옮긴다.
// (한 칸 스왑이 아니라 임의 위치 이동. moveHeaderColumn 은 별개 용도로 유지, 여기선 사용하지 않는다.)
// 원본 배열은 mutate 하지 않는다. 숨김 컬럼은 드래그 대상이 아니므로(행으로 렌더되지 않음)
// 그대로 남아 상대 위치만 유지된다.
const reorderHeaderColumns = (list, fromName, toName) => {
	if (!Array.isArray(list) || fromName === toName) return Array.isArray(list) ? list.slice() : [];

	const fromIndex = list.findIndex((c) => c?.name === fromName);
	const toIndex = list.findIndex((c) => c?.name === toName);
	if (fromIndex === -1 || toIndex === -1) return list.slice();

	const movingDown = fromIndex < toIndex;
	const moved = list[fromIndex];
	const without = list.filter((c) => c?.name !== fromName);
	const targetIndexAfterRemoval = without.findIndex((c) => c?.name === toName);
	const insertIndex = movingDown ? targetIndexAfterRemoval + 1 : targetIndexAfterRemoval;

	const next = without.slice();
	next.splice(insertIndex, 0, moved);
	return next;
};

// IMP-444 — codeList 에서 code 의 label/type 을 조회한다(없으면 code 자체를 label 로 사용).
const resolveHeaderColumnMeta = (code, codeListArr) => {
	const list = Array.isArray(codeListArr) ? codeListArr : [];
	const found = list.find((y) => y?.code === code);
	return { name: code, label: found ? found.name : code, type: found?.type || '' };
};

const CustomHeader = ({ showModal, title, defaultFilter, updateUserAdminHeader,
	callbackClose, clist, baseHeader = EMPTY_BASE_HEADER,
	extratrakey, exHeader, extratrakeyname, adminheaderprefix, user }) => {

	const [options, setOptions] = useState([]);
	const [seloptions, setSelOptions] = useState(exHeader ? exHeader.map(x => x.name) : []);
	const [selval, setSelVal] = useState("");
	const [prdOptionList, setPrdOptionList] = useState([]);
	const [selprdoption, setSelPrdOption] = useState("");
	const [selvaltf, setSelValTF] = useState(true);
	const [valtype, setValType] = useState("STRING");
	const [filter, setFilter] = useState(exHeader ? exHeader : []);
	const [codeList, setCodeList] = useState([]);
	const [dtOptionVal, setDtOptionVal] = useState({ opt: "last", lastVal: 7, lastValOpt: "d" });
	const [unified, setUnified] = useState([]);
	// IMP-444/리뷰 F-002 — 이번 세션에 사용자가 ×로 지운 '추가 컬럼' 이름. 화면이 넘기는
	// baseHeader 는 저장 전까지 갱신되지 않아 지운 추가 컬럼을 계속 포함하므로, 통합 목록
	// 재계산 시 이 목록을 빼 주지 않으면 지운 행이 즉시 되살아난다. 모달을 다시 열면 비운다(취소).
	const [removedAddedNames, setRemovedAddedNames] = useState([]);
	// IMP-444 스타일 피드백 — 행 hover/드래그 중 시각 구분(로직 아님, 표시 전용).
	const [hoverRowName, setHoverRowName] = useState(null);
	const [draggingRowName, setDraggingRowName] = useState(null);
	// IMP-444 피드백(드래그 UX) — dragover 중인 행 이름. 드롭 위치를 눈에 보이게 표시하는 용도(표시 전용).
	const [dropTargetName, setDropTargetName] = useState(null);
	// IMP-444 — 모달이 "닫힘→열림"으로 바뀐 시점 판정용. 열리는 순간에는 직전 미저장 편집(prev)을
	// 버리고 저장값 기준으로 다시 계산해야 "취소"가 의미를 갖는다(모달이 열려있는 동안의 편집은 보존).
	const prevShowModalRef = React.useRef(false);
	const dispatch = useDispatch();

	useEffect(() => {
		setCodes();
	}, [defaultFilter, clist]);

	useEffect(() => {
		setSelOptions(exHeader.map(x => x.name));
	}, [exHeader]);

	// IMP-444 — baseHeader/추가 컬럼/codeList 변화 시 통합 목록을 재계산한다.
	// 사용자가 이미 조정한 순서/표시 상태(prev)가 있으면 그것을 저장된 선호값처럼 취급해
	// 잃어버리지 않는다. 없으면 redux user.adminheaders[adminheaderprefix] 를 사용한다.
	useEffect(() => {
		// IMP-444 — "방금 열렸는지" 판정. 열리는 순간엔 prev(직전 미저장 편집)를 버리고
		// savedPrefs 기준으로 재계산한다(취소 동작). 열려 있는 동안의 편집은 prev 로 보존.
		const justOpened = showModal && !prevShowModalRef.current;
		prevShowModalRef.current = showModal;
		const selectedColumnObjs = (Array.isArray(seloptions) ? seloptions : []).map((code) => resolveHeaderColumnMeta(code, codeList));
		const savedPrefs = user?.adminheaders?.[adminheaderprefix];
		if (justOpened && removedAddedNames.length) setRemovedAddedNames([]);
		const dropped = justOpened ? [] : removedAddedNames;
		const effectiveBase = (Array.isArray(baseHeader) ? baseHeader : []).filter((b) => !dropped.includes(b?.name));
		setUnified((prev) => {
			const basis = (!justOpened && prev.length) ? toSavedHeaderPrefs(prev) : savedPrefs;
			const next = buildUnifiedHeaderList(effectiveBase, selectedColumnObjs, basis);
			return isSameHeaderList(prev, next) ? prev : next;
		});
	}, [showModal, baseHeader, seloptions, codeList, removedAddedNames]);

	useEffect(() => {
		// IMP-316 — codeList 각 항목의 code 접두사에 따라 group 계산.
		const oList = []
		codeList.map(x => {
			// BUG-155: 배열 subdocument 경로 필드는 목록 컬럼으로 표시 불가(공백) → 헤더 옵션에서 제외.
			if (!isDisplayableHeaderOption(x.code)) return;
			let group = t('common.headerGroup.basic');
			if (typeof x.code === 'string' && x.code.startsWith('customfieldvals.')) {
				group = t('common.headerGroup.custom');
			} else if (typeof x.code === 'string' && x.code.startsWith(extratrakeyname + '.')) {
				// category(제조이력/EMS/선적정보 등)는 RayCode 원본 데이터 그대로 raw 유지.
				group = x.category || t('common.headerGroup.extra');
			}
			oList.push({ code: x.code, title: x.name, group });
		});
		setOptions(oList);
	}, [codeList]);

	const setCodes = async () => {
		const codes = Array.isArray(clist) ? clist : []; // BUG-157: 비배열 가드
		const filterList = [...defaultFilter];
		const extradataList = codes.filter(x => x.type === extratrakey);
		// IMP-316: category 보존 — options useEffect 에서 group 계산 시 사용.
		extradataList.filter(x => filterList.push({ code: extratrakeyname + "." + x.code, name: x.name, type: x.datatype, category: x.category }));
		setCodeList(filterList);

		const optList = [];
		const oList = codes.filter(x => x.type === 'PRODUCTOPTION');
		oList.filter(x => optList.push({ code: x.name, title: x.name, type: 'OPTION' }));
		setPrdOptionList(optList);
	}

	// IMP-444/리뷰 F-002 — 화면들은 baseHeader 에 "기본 컬럼 + 추가 컬럼" 합본을 넘기고,
	// 추가 컬럼만 따로 exHeader 로도 넘긴다(예: AdminProductList 의
	// AdminProductListHeader.concat(exheader) → baseHeader={header}). baseHeader 만으로 기본/추가를
	// 판정하면 추가 컬럼도 항상 기본 컬럼으로 오분류돼, ×로 지워도 저장 payload 에서 빠지지 않고
	// visible:false 로 영구 잔존한다. 진짜 기본 컬럼 = baseHeader − exHeader.
	// (이번 세션에 콤보로 갓 추가한 컬럼은 아직 baseHeader 에 없으므로 자연히 추가 컬럼으로 판정된다.)
	const realBaseHeaderNames = React.useMemo(() => {
		const baseArr = Array.isArray(baseHeader) ? baseHeader : [];
		const exArr = Array.isArray(exHeader) ? exHeader : [];
		const exNames = new Set(exArr.map((x) => x?.name).filter(Boolean));
		return baseArr.map((b) => b?.name).filter((name) => name && !exNames.has(name));
	}, [baseHeader, exHeader]);

	// IMP-444 — 행 삭제 버튼 클릭. 판정은 seloptions 소속이 아니라 realBaseHeaderNames(진짜 기본
	// 컬럼) 소속 여부로 한다. 기본 컬럼(baseHeader 에 존재)은 재추가(콤보 재선택) 경로를 거쳐도
	// seloptions 에 이름이 들어갈 수 있어 seloptions 기준으로는 "추가 컬럼"으로 오분류된다(사전 리뷰
	// 지적, Major). 기본 컬럼이면 unified 에서 visible:false 로만 바꾸고(FR-007: 배열 제거 금지),
	// 재추가로 인해 seloptions 에 남아있던 잔상도 함께 제거한다. 기본 컬럼이 아니면 추가 컬럼으로
	// 보고 선택 해제한다(seloptions 에서 이름만 제거 → 저장 payload 에서 완전히 빠짐).
	const handleRemoveColumn = (col) => {
		if (!col || typeof col.name !== 'string') return;
		const isBaseColumn = realBaseHeaderNames.includes(col.name);
		if (!isBaseColumn) {
			setSelOptions((prev) => (Array.isArray(prev) ? prev : []).filter((c) => c !== col.name));
			setRemovedAddedNames((prev) => (prev.includes(col.name) ? prev : [...prev, col.name]));
			setUnified((prev) => (Array.isArray(prev) ? prev : []).filter((c) => c?.name !== col.name));
			return;
		}
		if (!canHideHeaderColumn(unified, col.name)) return;
		setUnified((prev) => (Array.isArray(prev) ? prev : []).map((c) =>
			(c?.name === col.name ? { ...c, visible: false } : c)));
		setSelOptions((prev) => (Array.isArray(prev) ? prev : []).filter((c) => c !== col.name));
	};

	// IMP-444 — '컬럼 추가' 콤보 변경. 새로 선택된 이름 중 이미 unified 에 있던(기본) 컬럼은
	// visible:true 로 되돌린다(콤보 재선택 = 다시 표시).
	const handleSelectOptionsChange = (vals) => {
		const codes = (Array.isArray(vals) ? vals : []).map((v) => v.code);
		const addedNames = codes.filter((c) => !seloptions.includes(c));
		setSelOptions(codes);
		if (addedNames.length) {
			setRemovedAddedNames((prev) => prev.filter((n) => !addedNames.includes(n)));
			setUnified((prev) => (Array.isArray(prev) ? prev : []).map((c) =>
				(addedNames.includes(c?.name) ? { ...c, visible: true } : c)));
		}
	};

	// IMP-444 — 드래그 정렬 핸들러. dataTransfer 가 없을 수 있으므로 optional chaining + try/catch 로 방어.
	// 회귀 방지: 삭제 컨트롤(data-remove) 위에서 mousedown 후 이동하면, 자식의 draggable={false} 와 무관하게
	// 브라우저가 가장 가까운 draggable 조상(행 자신)에서 드래그를 시작해 dragstart 가 행에서 발생한다.
	// onMouseDown 에서 stopPropagation() 하면 클릭 시퀀스(mousedown→mouseup→click)가 깨져 첫 클릭이 씹히므로,
	// 대신 여기서 이벤트 타겟이 삭제 컨트롤 내부인지 확인해 드래그 시작 자체를 막는다.
	const handleRowDragStart = (col) => (e) => {
		if (e.target?.closest?.('[data-remove]')) {
			e.preventDefault();
			return;
		}
		try {
			e.dataTransfer?.setData('text/plain', col.name);
			if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
		} catch (err) { /* jsdom/일부 환경 setData 미지원 방어 */ }
		setDraggingRowName(col?.name ?? null);
	};

	const handleRowDragEnd = () => {
		setDraggingRowName(null);
		setDropTargetName(null);
	};

	// 사용자 피드백(드래그 UX) — 행 위에서는 dropEffect 를 명시하고, 드래그 중인 행 자신을 제외하고
	// 드롭 대상으로 표시한다(경계선/배경으로 "영역 확정"을 눈에 보이게).
	const handleRowDragOver = (col) => (e) => {
		e.preventDefault();
		try {
			if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		} catch (err) { /* jsdom/일부 환경 dropEffect 미지원 방어 */ }
		if (col?.name && col.name !== draggingRowName) {
			setDropTargetName(col.name);
		}
	};

	// 사용자 피드백(드래그 UX) — 목록 스크롤 컨테이너 전체를 드롭 허용 영역으로 넓혀
	// 행 사이 여백/라벨/아이콘 위에서도 금지 커서가 뜨지 않게 한다(행별 표시는 그대로 유지).
	const handleListDragOver = (e) => {
		e.preventDefault();
		try {
			if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		} catch (err) { /* jsdom/일부 환경 dropEffect 미지원 방어 */ }
	};

	const handleRowDrop = (col) => (e) => {
		e.preventDefault();
		let sourceName;
		try {
			sourceName = e.dataTransfer?.getData('text/plain');
		} catch (err) {
			sourceName = undefined;
		}
		setDraggingRowName(null);
		setDropTargetName(null);
		if (!sourceName || sourceName === col.name) return;
		setUnified((prev) => reorderHeaderColumns(prev, sourceName, col.name));
	};

	const btnSave = () => {
		// IMP-444: 통합 목록(unified) 이 SSoT — 기본 컬럼 포함, 숨김은 visible:false 로 보존(FR-007).
		// IMP-236: action creator 가 `adminheaders.<prefix>` payload 책임을 가져감.
		// 호출 사이트는 prefix/filter 의도만 전달하면 됨. BE 계약(SEC-007/BUG-116) 변경 시
		// AuthAction.updateUserAdminHeader 한 곳만 수정.
		updateUserAdminHeader(adminheaderprefix, toSavedHeaderPrefs(unified));
		callbackClose(false);
	}

	// IMP-316 — 헤더 선택 Picker 재설계: 상단 Selected chip 영역 + 하단 react-select 그룹 검색 콤보.
	// GROUP_ORDER 는 group 계산부(위 useEffect)와 동일한 t() 결과 문자열을 사용해야 그룹 매칭이 일치한다.
	// '제조이력'/'EMS'/'선적정보' 는 RayCode category raw 값 그대로(번역 대상 아님).
	// IMP-316: RayCode category(제조이력/EMS/선적정보) 와의 결합 계약을 명시. category 명칭 변경 시 여기 동기화.
	const EXTRA_CATEGORY_ORDER = ['제조이력', 'EMS', '선적정보'];
	const GROUP_ORDER = [t('common.headerGroup.basic'), ...EXTRA_CATEGORY_ORDER, t('common.headerGroup.custom'), t('common.headerGroup.extra')];
	// IMP-444 — 콤보 제외 목록은 "현재 표시 중인 컬럼 이름"만(숨긴 기본 컬럼은 콤보에 다시 나타나야 함).
	const baseNames = visibleHeaderColumns(unified).map((c) => c?.name).filter(Boolean);
	// IMP-444/리뷰 F-003 — 숨긴 기본 컬럼은 화면 defaultFilter/커스텀필드 코드에 없어 콤보 후보에
	// 아예 등장하지 않았다. 숨김 항목을 후보로 합류시켜 "다시 표시" 경로를 복원한다.
	// options 뒤에 붙여 code 중복 시 기존 옵션의 title/group 이 우선(buildGroupedHeaderOptions 의
	// seenCodes 는 첫 등장을 채택)하게 한다. group 은 신규 i18n 키 없이 기존 basic 그룹을 재사용한다.
	const hiddenUnifiedOptions = (Array.isArray(unified) ? unified : [])
		.filter((c) => c?.visible === false && typeof c?.name === 'string')
		.map((c) => ({ code: c.name, title: c.label || c.name, group: t('common.headerGroup.basic') }));
	const grouped = buildGroupedHeaderOptions([...options, ...hiddenUnifiedOptions], GROUP_ORDER, baseNames, seloptions);
	const selectedObjs = options.filter(o => seloptions.includes(o.code));
	const visibleRows = visibleHeaderColumns(unified);

	return (
		<Modal show={showModal} size="xl" scrollable>
			<Modal.Header>
				<h2 className="modal-title">{title}</h2>
			</Modal.Header>
			<Modal.Body className="p-0">
				<ul className="listview simple-listview no-space border-0 m-2">
					<div className="form-group basic">
						<div className="input-wrapper">
							<Select
								isMulti
								options={grouped}
								value={selectedObjs}
								getOptionLabel={o => o.title || o.code}
								getOptionValue={o => o.code}
								onChange={handleSelectOptionsChange}
								placeholder={t('common.button.selectHeader')}
								closeMenuOnSelect={false}
								hideSelectedOptions={true}
								controlShouldRenderValue={false}
								isClearable={false}
								classNamePrefix="rg-header-picker"
								styles={{
									// IMP-316: 그룹 계층 시각화 — 그룹명 들여쓰기 + 아이템은 더 깊게 들여쓰기.
									groupHeading: (base) => ({ ...base, paddingLeft: 12, fontWeight: 700, fontSize: '12px' }),
									option: (base) => ({ ...base, paddingLeft: 28 }),
								}}
							/>
						</div>
					</div>
					{/* IMP-444 — 전체 컬럼 통합 목록: 표시 중인 컬럼만 행으로 렌더, 드래그로 순서 변경, 행별 삭제 버튼.
					    목록 영역에만 세로 스크롤(모달 높이 고정). */}
					<div className="form-group basic rg-header-list" style={{ height: '55vh', overflowY: 'auto' }} onDragOver={handleListDragOver}>
						{visibleRows.map((col) => {
							const canHide = canHideHeaderColumn(unified, col.name);
							const isHovered = hoverRowName === col.name;
							const isDragging = draggingRowName === col.name;
							const isDropTarget = !isDragging && dropTargetName === col.name;
							return (
								<div
									key={col.name}
									className="rg-header-row d-flex align-items-center justify-content-between mb-1"
									data-name={col.name}
									draggable
									onDragStart={handleRowDragStart(col)}
									onDragEnd={handleRowDragEnd}
									onDragOver={handleRowDragOver(col)}
									onDrop={handleRowDrop(col)}
									onMouseEnter={() => setHoverRowName(col.name)}
									onMouseLeave={() => setHoverRowName((prev) => (prev === col.name ? null : prev))}
									style={{
										padding: '2px 8px',
										fontSize: 14,
										borderTop: isDropTarget ? '2px solid #3b82f6' : '2px solid transparent',
										borderBottom: isDropTarget ? '2px solid #3b82f6' : '2px solid transparent',
										backgroundColor: isDropTarget ? '#eaf2ff' : (isHovered ? '#f5f5f5' : 'transparent'),
										opacity: isDragging ? 0.5 : 1,
									}}
								>
									<div className="d-flex align-items-center" style={{ pointerEvents: 'none' }}>
										<span className="raycur" style={{ fontSize: '16px', lineHeight: 0 }}>
											<ion-icon name="reorder-two-outline"></ion-icon>
										</span>
										<span className="ps-2">{col.label || col.name}</span>
									</div>
									<span
										role="button"
										className="raycur rg-header-remove"
										data-remove
										disabled={!canHide}
										aria-disabled={!canHide}
										draggable={false}
										onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
										onClick={() => canHide && handleRemoveColumn(col)}
										style={{
											display: 'inline-flex',
											alignItems: 'center',
											justifyContent: 'center',
											minWidth: 28,
											minHeight: 28,
											fontSize: '16px',
											lineHeight: 0,
											color: !canHide ? '#ccc' : (isHovered ? '#333' : '#999'),
											cursor: !canHide ? 'not-allowed' : 'pointer',
										}}
									>
										<ion-icon name="close-outline"></ion-icon>
									</span>
								</div>
							);
						})}
					</div>
				</ul>
			</Modal.Body>
			<Modal.Footer>
				<button className="btn btn-block btn-primary mb-0 mt-0" onClick={() => btnSave()}>{t('common.button.save')}</button>
				<button className="btn btn-block btn-dark" onClick={() => callbackClose(false)}>{t('common.button.close')}</button>
			</Modal.Footer>
		</Modal>
	);
}

const mapState = (state) => {
	const user = state.AuthReducer.user;
	const clist = state.RayCodeReducer.items;
	return { user, clist };
};

const mapDispatch = (dispatch) => ({
	// IMP-236: dotted payload 추상화. 호출 사이트는 prefix/filter 의도만 전달.
	updateUserAdminHeader: (prefix, filter) => dispatch(AuthAction.updateUserAdminHeader(prefix, filter)),
});

export default connect(mapState, mapDispatch)(CustomHeader);
